import { useMemo } from 'react';
import { OrganizationType, organizationTypeLabel } from '@signalvote/shared';
import type { ComplianceProfileDto } from '@signalvote/shared';

/**
 * #479 (#477 cluster A — W1/W2) — the ONE shared organization-subtype options derivation, consumed
 * by BOTH the ContactDetail edit picker and the ClassifyOrganizationBody triage picker (mirroring
 * `useElectionDesignationOptions`), so the two dropdowns can never drift and neither renders the
 * full 7-value {@link OrganizationType} enum on a jurisdiction that does not model it — the #349
 * "wrong-silent-classification" leak, where a VA/AL treasurer could tag a donor "Labor union" /
 * "Candidate committee" and persist an inert-but-durable value that a later ruleset switch would
 * begin driving prohibited-source/schedule logic against with no re-review.
 *
 * The offered set AND its order come straight from the server-declared `supportedOrganizationTypes`
 * on the {@link ComplianceProfileDto} (#478). That per-jurisdiction set is a compliance judgment,
 * not a client lookup: Alabama declares `[Corporation, Pac]` though its limits reference neither (5
 * of 7 subtypes fold to EDI source 05/Other); federal declares the committee set but omits a plain
 * "Other organization"; Virginia declares `[]` (it routes by office level and uses the axis zero
 * times). A missing/unloaded profile offers nothing — never the full enum — keeping the leak closed.
 */
export interface OrganizationTypeOption {
  /** The subtype persisted on `FinanceContact.organizationType`. */
  value: OrganizationType;
  /** Treasurer-first display label (`organizationTypeLabel`). */
  label: string;
  /**
   * True only for a value shown because it is the contact's already-persisted subtype AND the
   * active jurisdiction no longer models it. Surfaced for re-review, never silently dropped — a
   * ruleset switch must not reclassify or lose it (#477 decision 2: show-but-don't-offer).
   */
  unsupported: boolean;
}

/**
 * Pure derivation behind {@link useOrganizationTypeOptions} — exported so it can be unit-tested
 * without React. `currentValue` is the subtype currently selected/persisted on the contact; when it
 * is set but absent from the jurisdiction's supported set it is appended (flagged `unsupported`) so
 * the `<select>` can always render it and the treasurer is prompted to re-review rather than lose it.
 */
export function deriveOrganizationTypeOptions(
  profile: ComplianceProfileDto | null | undefined,
  currentValue?: OrganizationType | '' | null,
): OrganizationTypeOption[] {
  const supported = profile?.supportedOrganizationTypes ?? [];
  const options: OrganizationTypeOption[] = supported.map((value) => ({
    value,
    label: organizationTypeLabel(value),
    unsupported: false,
  }));

  if (currentValue && !supported.includes(currentValue)) {
    options.push({
      value: currentValue,
      label: organizationTypeLabel(currentValue),
      unsupported: true,
    });
  }

  return options;
}

/**
 * React hook wrapper around {@link deriveOrganizationTypeOptions} — memoized on the inputs that
 * change the list (the declared set reference is stable across renders within a query-cache entry).
 */
export function useOrganizationTypeOptions(
  profile: ComplianceProfileDto | null | undefined,
  currentValue?: OrganizationType | '' | null,
): OrganizationTypeOption[] {
  return useMemo(
    () => deriveOrganizationTypeOptions(profile, currentValue),
    [profile?.supportedOrganizationTypes, currentValue],
  );
}
